/**
 * save-slot-picker.ts — overlay shown when "Save Game" is picked from
 * the in-game menu (v2M2). Lists all four slots with their current
 * contents and a Save / Overwrite action per row.
 *
 * For named slots the player is prompted for a label via window.prompt;
 * quicksave skips the prompt and stores `label: null`. Overwriting an
 * occupied named slot confirms first.
 *
 * Per UI_UX §9.3 the in-game menu remains open behind the picker — the
 * sim stays paused while the player decides. Picker closes back to the
 * menu on save-complete or cancel; main.ts decides whether to resume
 * after.
 */

import { exportBlobToFile } from '../save/file-io';
import {
  serializeGameState,
  type SaveBlobHeader,
} from '../save/serialize';
import {
  ALL_SLOT_KEYS,
  type SaveSlotKey,
  type SaveStorage,
} from '../save/storage';
import type { GameState } from '../state/types/index';
import { getAudioEngine } from '../audio';

export interface SaveSlotPickerCallbacks {
  storage: SaveStorage;
  /**
   * Current GameState getter — re-evaluated at save time, not at picker
   * mount, so a save reflects the moment the player clicked Save.
   * (The sim is paused while the menu is open so the value is stable
   * either way, but the getter pattern future-proofs against the
   * picker becoming async.)
   */
  getState: () => GameState;
  /** Called after a successful save with the slot it landed in. */
  onSaved(slot: SaveSlotKey): void;
  /** Called when the player cancels without saving. */
  onCancel(): void;
}

export interface SaveSlotPickerHandle {
  destroy(): void;
}

const SLOT_DISPLAY_NAME: Readonly<Record<SaveSlotKey, string>> = {
  'slot:1': 'Slot 1',
  'slot:2': 'Slot 2',
  'slot:3': 'Slot 3',
  quicksave: 'Quicksave',
  autosave: 'Autosave',
};

function formatRelativeTime(savedAtMs: number): string {
  const deltaSec = Math.max(0, Math.round((Date.now() - savedAtMs) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.round(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.round(deltaHr / 24);
  return `${deltaDay}d ago`;
}

function describeOccupiedSlot(header: SaveBlobHeader): string {
  const label = header.label ?? 'Saved';
  return `${label} · Tick ${header.tick} · ${formatRelativeTime(header.savedAt)}`;
}

export function mountSaveSlotPicker(
  parent: HTMLElement,
  callbacks: SaveSlotPickerCallbacks,
): SaveSlotPickerHandle {
  const overlay = document.createElement('div');
  overlay.id = 'save-slot-picker-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(5, 6, 10, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1100',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
    // v4M1.11 — 150 ms opacity fade on mount + destroy (see in-game-menu.ts).
    opacity: '0',
    transition: 'opacity 150ms ease-out',
  } as Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minWidth: '480px',
    maxWidth: '600px',
    padding: '28px 32px',
    background: '#0E131C',
    border: '1px solid #2A3344',
    borderRadius: '2px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(card);

  const heading = document.createElement('div');
  heading.textContent = 'SAVE GAME';
  Object.assign(heading.style, {
    fontSize: '14px',
    letterSpacing: '0.18em',
    color: '#7E8799',
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(heading);

  function performSave(slot: SaveSlotKey): void {
    const isQuicksave = slot === 'quicksave';
    let label: string | null = null;
    if (!isQuicksave) {
      // Named slot — prompt for a label. The browser prompt blocks until
      // the user submits or cancels; OK with empty input keeps label null.
      const input = window.prompt(
        'Save label (optional):',
        '',
      );
      if (input === null) return; // cancel — leave picker open
      label = input.trim() === '' ? null : input.trim();
    }
    const blob = serializeGameState(callbacks.getState(), { label });
    callbacks.storage.write(slot, blob);
    callbacks.onSaved(slot);
  }

  function makeSlotRow(slot: SaveSlotKey, header: SaveBlobHeader | null): HTMLElement {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 14px',
      background: '#05060A',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      // v4M1.11 — hover highlight. Action lives on the per-row button, but
      // the full row signals interactivity by lifting the background +
      // border on mouseenter. 120 ms transition keeps the response feeling
      // immediate without flickering.
      transition: 'background 120ms ease-out, border-color 120ms ease-out',
    } as Partial<CSSStyleDeclaration>);
    row.addEventListener('mouseenter', () => {
      row.style.background = '#0A1018';
      row.style.borderColor = '#3F4C66';
    });
    row.addEventListener('mouseleave', () => {
      row.style.background = '#05060A';
      row.style.borderColor = '#2A3344';
    });

    const left = document.createElement('div');
    Object.assign(left.style, {
      flex: '0 0 100px',
      fontWeight: '600',
      fontSize: '13px',
      letterSpacing: '0.06em',
    } as Partial<CSSStyleDeclaration>);
    left.textContent = SLOT_DISPLAY_NAME[slot];
    row.appendChild(left);

    const middle = document.createElement('div');
    Object.assign(middle.style, {
      flex: '1 1 auto',
      fontSize: '12px',
      color: header ? '#D7DEEA' : '#7E8799',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    } as Partial<CSSStyleDeclaration>);
    // v4M1.4 — `desc` is kept in scope so the rename action below can
    // refresh the visible label without re-mounting the entire row.
    let desc: HTMLSpanElement | null = null;
    if (header) {
      const badge = makeOutcomeBadge(header.gameOutcome);
      if (badge) middle.appendChild(badge);
      desc = document.createElement('span');
      desc.textContent = describeOccupiedSlot(header);
      middle.appendChild(desc);
    } else {
      middle.textContent = 'Empty';
    }
    row.appendChild(middle);

    // v4M1.4 — Rename affordance for occupied named slots (slot:1/2/3).
    // Quicksave + Autosave are auto-titled and not renameable. The
    // pencil-text button opens a window.prompt seeded with the current
    // label; on confirm, the stored blob's header.label is updated
    // and the row's visible description is refreshed in place. The
    // body of the save is untouched.
    const isNamedSlot =
      slot === 'slot:1' || slot === 'slot:2' || slot === 'slot:3';
    if (isNamedSlot && header !== null) {
      const renameBtn = document.createElement('button');
      renameBtn.type = 'button';
      renameBtn.textContent = 'Rename';
      renameBtn.title =
        'Edit the label on this saved slot. The save body is untouched; only the label changes.';
      Object.assign(renameBtn.style, {
        flex: '0 0 auto',
        padding: '6px 10px',
        background: 'transparent',
        color: '#8ea0c8',
        border: '1px solid #2A3344',
        borderRadius: '2px',
        fontFamily: 'inherit',
        fontSize: '11px',
        letterSpacing: '0.04em',
        cursor: 'pointer',
      } as Partial<CSSStyleDeclaration>);
      renameBtn.addEventListener('click', () => {
        const next = window.prompt('Rename this save:', header.label ?? '');
        if (next === null) return; // cancel
        const trimmed = next.trim();
        const newLabel = trimmed === '' ? null : trimmed;
        const blob = callbacks.storage.read(slot);
        if (blob === null) return; // race: slot vanished between picker mount and click
        blob.header = { ...blob.header, label: newLabel };
        callbacks.storage.write(slot, blob);
        // Mutate the closure-captured header so subsequent renames
        // start from the new label, and refresh the visible row.
        (header as { label: string | null }).label = newLabel;
        if (desc) desc.textContent = describeOccupiedSlot(header);
      });
      row.appendChild(renameBtn);
    }

    const action = document.createElement('button');
    action.type = 'button';
    action.textContent = header ? 'Overwrite' : 'Save';
    action.title = header
      ? 'Replace the save in this slot with the current game. Confirms before overwriting.'
      : 'Save the current game to this empty slot.';
    Object.assign(action.style, {
      flex: '0 0 auto',
      padding: '8px 16px',
      background: '#2B4A7A',
      color: '#FFFFFF',
      border: '1px solid #3F6FA8',
      borderRadius: '2px',
      fontFamily: 'inherit',
      fontSize: '12px',
      letterSpacing: '0.06em',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    action.addEventListener('click', () => {
      if (header && !confirm(`Overwrite ${SLOT_DISPLAY_NAME[slot]}?`)) return;
      performSave(slot);
    });
    row.appendChild(action);

    return row;
  }

  // v4M1.4 — the `autosave` slot is owned by the session auto-save
  // timer; players never manually pick it from the Save picker. Filter
  // it out here. It is still visible in the Load picker for restoring
  // from the latest auto-saved snapshot.
  for (const entry of callbacks.storage.listSlots()) {
    if (entry.key === 'autosave') continue;
    card.appendChild(makeSlotRow(entry.key, entry.header));
  }

  const footer = document.createElement('div');
  Object.assign(footer.style, {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: '8px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(footer);

  // v4M1.4 — Export current game to .susave file. Doesn't touch slots;
  // direct serialise + download. The OS save dialog handles filename
  // editing if the player wants to override the default.
  const exportBtn = document.createElement('button');
  exportBtn.type = 'button';
  exportBtn.textContent = 'Export to .susave…';
  exportBtn.title =
    'Download the current game state as a .susave file. Independent of slots — exports the live state, not a stored slot.';
  Object.assign(exportBtn.style, {
    padding: '10px 20px',
    background: '#0E131C',
    color: '#D7DEEA',
    border: '1px solid #2A3344',
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '13px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  exportBtn.addEventListener('click', () => {
    const blob = serializeGameState(callbacks.getState(), { label: 'Exported' });
    exportBlobToFile(blob);
  });
  footer.appendChild(exportBtn);

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.title = 'Close the slot picker without saving.';
  Object.assign(cancelBtn.style, {
    padding: '10px 20px',
    background: '#0E131C',
    color: '#D7DEEA',
    border: '1px solid #2A3344',
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '13px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  cancelBtn.addEventListener('click', callbacks.onCancel);
  footer.appendChild(cancelBtn);

  // Sanity-check that the expected number of slot keys exist. The save
  // picker filters out `autosave` (handled by the session, not the
  // player), so we render 4 of the 5 storage slots. v4M1.4 bumped the
  // total to 5 when the autosave slot landed.
  if (ALL_SLOT_KEYS.length !== 5) {
    console.warn('save-slot-picker: expected 5 storage slots, got', ALL_SLOT_KEYS.length);
  }

  parent.appendChild(overlay);
  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
  });
  getAudioEngine().playCue('ui:panel-open');
  return {
    destroy(): void {
      getAudioEngine().playCue('ui:panel-close');
      overlay.style.opacity = '0';
      setTimeout(() => overlay.remove(), 150);
    },
  };
}

/**
 * v3M12.4 — small status badge for completed-game saves. Returns
 * `null` for in-progress games (no decoration). Per WIN_CONDITIONS
 * §7: green check for victory, red ✗ for defeat.
 */
function makeOutcomeBadge(
  outcome: 'in-progress' | 'victory' | 'defeat',
): HTMLElement | null {
  if (outcome === 'in-progress') return null;
  const badge = document.createElement('span');
  badge.textContent = outcome === 'victory' ? '✓' : '✗';
  Object.assign(badge.style, {
    color: outcome === 'victory' ? '#63C77C' : '#D24548',
    fontWeight: '700',
    fontSize: '14px',
    flex: '0 0 auto',
  } as Partial<CSSStyleDeclaration>);
  badge.title = outcome === 'victory' ? 'Victory' : 'Defeat';
  return badge;
}
